import { useCallback, useRef, useState } from 'react';
import { validateServiceCertification } from './api/certificationValidation';
import {
  CERTIFICATION_SERVICE_CODE_BY_NAME,
  CertificationValidationResult,
  CertificationValidationStatus,
} from '../types/certificationValidation';

export interface ServiceCertificationState {
  status: 'idle' | 'validating' | CertificationValidationStatus;
  attempt: number;
  result: CertificationValidationResult | null;
  error: string | null;
}

const IDLE_STATE: ServiceCertificationState = {
  status: 'idle',
  attempt: 0,
  result: null,
  error: null,
};

const MAX_ATTEMPTS = 3;

function fileKey(file: File): string {
  return `${file.name}_${file.size}_${file.lastModified}`;
}

/**
 * Orquesta la validación IA de un certificado por servicio (Pareja/Familia/OV):
 * - `validateCertificate` se llama cuando la psicóloga sube (o reemplaza) el
 *   certificado principal — siempre arranca en intento 1, incluso si ya había
 *   un resultado previo para ese servicio (reseteo al subir un documento distinto).
 * - `submitAdditionalDocument` se llama cuando responde a un pedido de
 *   `needs_additional_document`, incrementando el intento (tope: 3, luego
 *   fuerza rejected_escalated aunque el modelo diga lo contrario).
 */
export function useValidateServiceCertification() {
  const [states, setStates] = useState<Record<string, ServiceCertificationState>>({});
  // Última key del certificado principal validado por servicio (para detectar reemplazo).
  const certificateKeysRef = useRef<Record<string, string>>({});

  const patch = useCallback((service: string, next: Partial<ServiceCertificationState>) => {
    setStates((prev) => ({ ...prev, [service]: { ...(prev[service] ?? IDLE_STATE), ...next } }));
  }, []);

  const validateCertificate = useCallback(
    async (service: string, certificateFile: File): Promise<CertificationValidationResult> => {
      const code = CERTIFICATION_SERVICE_CODE_BY_NAME[service];
      if (!code) throw new Error(`"${service}" no tiene validación IA configurada.`);

      certificateKeysRef.current[service] = fileKey(certificateFile);
      patch(service, { status: 'validating', attempt: 1, result: null, error: null });

      try {
        const result = await validateServiceCertification({
          service: code,
          attempt: 1,
          certificateFile,
        });
        patch(service, { status: result.status, attempt: 1, result, error: null });
        return result;
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Error al validar el certificado';
        patch(service, { status: 'idle', attempt: 0, result: null, error: message });
        throw e;
      }
    },
    [patch],
  );

  const submitAdditionalDocument = useCallback(
    async (
      service: string,
      certificateFile: File,
      additionalDocumentFile: File,
    ): Promise<CertificationValidationResult> => {
      const code = CERTIFICATION_SERVICE_CODE_BY_NAME[service];
      if (!code) throw new Error(`"${service}" no tiene validación IA configurada.`);

      // Si el certificado principal cambió sin pasar por validateCertificate (no debería
      // ocurrir vía UI normal), tratamos esto como parte del mismo intento en curso.
      const current = states[service] ?? IDLE_STATE;
      const nextAttempt = Math.min(current.attempt + 1, MAX_ATTEMPTS) as 1 | 2 | 3;
      patch(service, { status: 'validating', attempt: nextAttempt });

      try {
        const result = await validateServiceCertification({
          service: code,
          attempt: nextAttempt,
          certificateFile,
          additionalDocumentFile,
        });
        // Se agotaron los intentos automáticos: si el modelo sigue pidiendo más
        // documentación en el intento 3, se fuerza el escalamiento.
        const finalStatus: CertificationValidationStatus =
          nextAttempt >= MAX_ATTEMPTS && result.status === 'needs_additional_document'
            ? 'rejected_escalated'
            : result.status;
        const finalResult: CertificationValidationResult = { ...result, status: finalStatus, attempt: nextAttempt };
        patch(service, { status: finalStatus, attempt: nextAttempt, result: finalResult, error: null });
        return finalResult;
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Error al validar el certificado';
        patch(service, { status: current.status, error: message });
        throw e;
      }
    },
    [patch, states],
  );

  const reset = useCallback((service: string) => {
    delete certificateKeysRef.current[service];
    setStates((prev) => {
      const next = { ...prev };
      delete next[service];
      return next;
    });
  }, []);

  const resetAll = useCallback((services: string[]) => {
    services.forEach((service) => {
      delete certificateKeysRef.current[service];
    });
    setStates((prev) => {
      const next = { ...prev };
      services.forEach((service) => delete next[service]);
      return next;
    });
  }, []);

  return { states, validateCertificate, submitAdditionalDocument, reset, resetAll };
}
